import React from "react";
import { QuickJumpNavProps, StepStatus } from "../../common/types";
import { CheckIcon } from "../../common/Icons";

const QuickJumpNav: React.FC<QuickJumpNavProps> = ({
  steps,
  currentViewedStepId,
  onStepSelect,
  isMobileBottomNavActive,
  stepCompletionStatus,
}) => {
  if (!steps || steps.length === 0) {
    return null;
  }

  // If MobileBottomNav is active, QJN sits above it, accounting for MobileBottomNav's full height (4rem + safe area).
  // Otherwise, QJN is at bottom-0 (and content padding in App.tsx will handle safe area for content).
  const bottomClass = isMobileBottomNavActive
    ? "bottom-[calc(4rem+env(safe-area-inset-bottom))]"
    : "bottom-0";

  const wrapperClasses = `
    md:hidden sticky ${bottomClass} z-40 
    w-full bg-gray-50 shadow-lg 
    py-2 px-2 
    transition-transform duration-300 ease-in-out h-14
  `; // z-index 40 to be below MBN (z-50) if they were to visually stack differently.

  return (
    <div
      className={wrapperClasses}
      role="navigation"
      aria-label="Quick step navigation"
    >
      <ol className="flex justify-around items-center w-full h-full px-1">
        {steps.map((step, index) => {
          const IconComponent = step.icon;
          const isCompleted =
            step.status === StepStatus.COMPLETED ||
            stepCompletionStatus[step.id] === true;
          const isCurrent = step.id === currentViewedStepId;

          let circleClasses =
            "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ease-in-out focus:outline-none ring-offset-1 shrink-0";
          let iconClasses = "w-5 h-5";
          let effectiveLineColor = "bg-gray-300";

          if (isCurrent && !isCompleted) {
            circleClasses += " bg-[#36a3f7]";
            iconClasses += " text-white";
            effectiveLineColor = "bg-green-400";
          } else if (isCompleted) {
            circleClasses += " bg-green-500 hover:bg-green-600";
            iconClasses += " text-white";
            effectiveLineColor = "bg-green-400";
          } else {
            circleClasses +=
              " bg-white border-2 border-gray-300 hover:border-gray-400";
            iconClasses += " text-gray-400";
          }

          if (isCurrent) {
            if (isCompleted) {
              circleClasses += " ring-2 ring-green-500 focus:ring-green-600";
            } else {
              circleClasses += " ring-2 ring-[#36a3f7] focus:ring-[#36a3f7]";
            }
          }

          return (
            <React.Fragment key={step.id}>
              <li className="flex-shrink-0">
                <button
                  onClick={() => onStepSelect(step.id)}
                  className={circleClasses}
                  aria-label={`Go to step: ${step.title}`}
                  aria-current={isCurrent ? "step" : undefined}
                  title={step.title}
                >
                  {isCompleted ? (
                    <CheckIcon className={iconClasses} />
                  ) : (
                    <IconComponent className={iconClasses} />
                  )}
                </button>
              </li>
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 flex-grow ${effectiveLineColor} transition-colors duration-200 max-w-[2rem] sm:max-w-[3rem] rounded`}
                ></div>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
};

export default QuickJumpNav;
